import Button from '@/components/Button';
import { RarityBadge } from '@/components/RarityBadge';
import RenameDat from '@/components/RenameDat';
import ShareMedia from '@/components/ShareMedia';
import TransferDat from '@/components/TransferDat';
import type { LazbubuListQuery } from '@/gql/graph/__generated__/request';
import application from '@/stores/application';
import serverConfig from '@/stores/serverConfig';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

type LazbubuItem = LazbubuListQuery['lazbubuList'][number];
type Status = 'incubating' | 'growing' | 'matured' | 'transferred';

// Mock 数据已移除，页面改为服务端数据

// Styled Components
const CardContainer = styled.div`
    width: 280px;
    background: white;
    border-radius: 16px;
    overflow: hidden;
    flex-shrink: 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    padding: 10px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    }
`;

const CharacterSection = styled.div`
    position: relative;

    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

// moved to public component RarityBadge

const CharacterImage = styled.img`
    width: 100%;
    height: fit-content;

    border-radius: 10px;

    overflow: hidden;

    max-width: 240px;
    max-height: 240px;
`;

const InfoSection = styled.div`
    padding: 10px;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
`;

const CharacterName = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    text-align: center;
    margin: 0 0 12px 0;
`;

const StatusTag = styled.div<{ status: Status }>`
    display: inline-block;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 12px;
    /* font-weight: 600; */
    text-transform: capitalize;

    display: block;
    width: fit-content;
    color: ${(props) => {
        switch (props.status) {
            case 'growing':
                return 'rgba(0, 186, 16, 1)';
            case 'matured':
                return 'rgba(255, 56, 60, 1)';

            default:
                return '#757575';
        }
    }};
    background: ${(props) => {
        switch (props.status) {
            case 'growing':
                return 'rgba(0, 186, 16, 0.1)';
            case 'matured':
                return 'rgba(255, 56, 60, 0.1)';

            default:
                return '#F5F5F5';
        }
    }};
`;

// Attributes UI temporarily removed per backend data structure

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    width: 100%;

    button {
        flex: 1;
    }
`;

const TransferredContent = styled.div`
    width: 100%;
    height: 38px;
    border-radius: 12px;
    background-color: rgba(235, 239, 254, 1);
    color: #000;
    display: flex;

    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
`;

const StyledRarityBadge = styled(RarityBadge)`
    position: absolute;
    top: 12px;
    left: 12px;
`;

const StyledShareMedia = styled(ShareMedia)`
    position: absolute;
    top: 12px;
    right: 12px;
`;

interface Props {
    data: LazbubuItem;
    isTransfered?: boolean;
    onTransfer?: () => void;
    onRenameSuccess?: () => void;
}

// Lazbubu 卡片组件（接收 lazbubuList() 的单项数据）
export const LazbubuCard: React.FC<Props> = ({
    data,
    isTransfered,
    onTransfer,
    onRenameSuccess,
}) => {
    const history = useHistory();
    // Modal 状态管理
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

    const handleTransferClick = () => {
        setIsTransferModalOpen(true);
    };

    // 处理重命名确认
    const handleRenameConfirm = () => {
        // 这里可以添加重命名的逻辑
        setIsRenameModalOpen(false);
        if (onRenameSuccess) {
            onRenameSuccess();
        }
    };

    // 处理转移确认
    const handleTransferConfirm = (receiverAddress: string) => {
        console.log('Transfer confirmed:', {
            receiverAddress,
            lazbubu: data,
        });
        // 这里可以添加转移的逻辑
        setIsTransferModalOpen(false);
    };

    const displayName = data.name || data.masterName || 'Unnamed';
    const status: Status = data.mature ? 'matured' : 'growing';

    const img = `${data.image}`;

    return (
        <CardContainer>
            {/* 角色展示区域 */}
            <CharacterSection>
                <StyledRarityBadge rarity={data.rarity} />
                <CharacterImage src={img} />
                <StyledShareMedia
                    url={window.location.href}
                    text={`I'm having a great time with my Lazbubu @LazbubuAI. Come join us!`}
                    onClick={() => {}}
                />
            </CharacterSection>

            {/* 信息区域 */}
            <InfoSection>
                {/* 角色名称 */}
                <CharacterName>{displayName}</CharacterName>

                {/* 状态标签 */}
                <StatusTag status={status}>{status}</StatusTag>

                {/* 属性标签 - 暂时移除，服务端无 attributes 字段 */}
                {/* <AttributesContainer></AttributesContainer> */}

                {/* 按钮区域 - 保持原有逻辑 */}
                {isTransfered ? (
                    <ButtonContainer>
                        <TransferredContent>Transferred</TransferredContent>
                    </ButtonContainer>
                ) : (
                    <ButtonContainer>
                        <Button
                            type="primary"
                            size="small"
                            className="flex-1"
                            onClick={() => {
                                if (data.needSetNames) {
                                    setIsRenameModalOpen(true);
                                } else {
                                    history.push(`/lazbubu/${data.tokenId}`);
                                    application.closeIncubatorDrawer();
                                }
                            }}
                        >
                            Train
                        </Button>
                        {/* Transfer 按钮 - 只在 matured 状态显示 */}
                        {data.mature && (
                            <Button
                                type="outline"
                                size="small"
                                onClick={handleTransferClick}
                            >
                                Transfer
                            </Button>
                        )}
                    </ButtonContainer>
                )}
            </InfoSection>

            {/* RenameDat Modal */}
            <RenameDat
                isOpen={isRenameModalOpen}
                onClose={() => setIsRenameModalOpen(false)}
                name={displayName}
                avatar={img}
                datAddress={
                    serverConfig.config?.contracts.dataAnchoringTokenAddress ||
                    ''
                }
                onConfirmSuccess={handleRenameConfirm}
                uuid={data.uuid}
                tokenId={data.tokenId}
            />

            {/* TransferDat Modal */}
            <TransferDat
                isOpen={isTransferModalOpen}
                onClose={() => setIsTransferModalOpen(false)}
                name={displayName}
                avatar={img}
                datId={data.tokenId}
                datAddress={
                    serverConfig.config?.contracts.dataAnchoringTokenAddress ||
                    ''
                }
                onConfirm={handleTransferConfirm}
                onTransferSuccess={onTransfer}
            />
        </CardContainer>
    );
};
